/**
 * The SYQEL platform's entitlement token — the account-wide record of what an account owns, issued by
 * the platform's `/v1/claim` (docs/PER_APP_PLANS.md in syqel-platform). A plan bought on syqel.com/pricing
 * (store keys `art_monthly` / `art_yearly`) lands here as `apps.art` and never passes through syqel.art's
 * own checkout, so this token is how SYQEL Art learns about it.
 *
 * Format: `base64url(payload JSON) "." base64url(Ed25519 signature over the payload bytes)` — not a JWT,
 * no prefix, one pinned key (the same key SYQEL Live pins in its Rust account module).
 *
 * Dependency-free on purpose: the signature check is passed in (`verify`), so the rules here run under
 * node in the tests with a TEST key, and the shipped bundle only ever trusts SYQEL_PLATFORM_KEYS.
 */

/** The platform's signing key (raw 32-byte Ed25519 public key, base64url). */
export const SYQEL_PLATFORM_KEYS: readonly string[] = ['WuhEDK1qMzKbH1vjxO7t8jSXIudeem7cR8oucIMM-gE']

export interface AppPlan {
  plan: string
  until: number | null
}

export interface SyqelClaims {
  uid: string
  tier: string
  subscriber?: boolean
  apps?: { desktop?: AppPlan; live?: AppPlan; art?: AppPlan }
  lifetime?: string[]
  iat: number
  exp: number
}

export type Ed25519Verify = (sig: Uint8Array, msg: Uint8Array, pub: Uint8Array) => Promise<boolean>

export function b64uToBytes(x: string): Uint8Array {
  const b64 = x.replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='))
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** The claims iff the token is signed by one of `keys` — expiry is NOT judged here (see artPaid). */
export async function readSyqelToken(
  token: string,
  verify: Ed25519Verify,
  keys: readonly string[] = SYQEL_PLATFORM_KEYS
): Promise<SyqelClaims | null> {
  const parts = token.split('.')
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null
  let payload: Uint8Array
  let sig: Uint8Array
  try {
    payload = b64uToBytes(parts[0])
    sig = b64uToBytes(parts[1])
  } catch {
    return null
  }
  if (sig.length !== 64) return null
  let signed = false
  for (const key of keys) {
    try {
      if (await verify(sig, payload, b64uToBytes(key))) { signed = true; break }
    } catch { /* malformed key or signature: not this key */ }
  }
  if (!signed) return null
  try {
    const claims = JSON.parse(new TextDecoder().decode(payload)) as SyqelClaims
    if (typeof claims !== 'object' || claims === null) return null
    if (typeof claims.exp !== 'number' || typeof claims.iat !== 'number') return null
    return claims
  } catch {
    return null
  }
}

/**
 * Does this account own SYQEL Art right now? The platform's contract, read the way SYQEL Live reads its
 * own app: with per-app plans present, Art is paid iff `apps.art` exists and runs to `until` (null = no
 * end: lifetime, legacy Pro / Deluxe / Elite — the platform folds those in). Any plan value counts, so a
 * plan name added later never takes a paid app away. Without `apps` (an older platform, or one that could
 * not read purchases) the shared tier decides, as it did before per-app plans. Never paid past `exp`:
 * the token's validity window is its offline grace.
 */
export function artPaid(claims: SyqelClaims, nowSec: number): boolean {
  if (nowSec >= claims.exp) return false
  const apps = claims.apps
  if (apps) {
    const art = apps.art
    if (!art) return false
    return art.until === null || art.until > nowSec
  }
  return claims.tier === 'pro' || claims.subscriber === true
}

/**
 * The token to keep when a refresh arrives. The platform omits `apps` when it could not read purchases;
 * that must not take a still-running Art plan away from the same account (SYQEL Live's adopt rule).
 */
export function adoptSyqelToken(prev: SyqelClaims | null, fresh: SyqelClaims, nowSec: number): 'fresh' | 'prev' {
  if (fresh.apps || !prev || prev.uid !== fresh.uid) return 'fresh'
  const art = prev.apps?.art
  const prevRunning = !!art && nowSec < prev.exp && (art.until === null || art.until > nowSec)
  return prevRunning ? 'prev' : 'fresh'
}

/**
 * Art is paid when ANY verified source says so: syqel.art's own checkout (`checkout`: null = its Worker
 * couldn't answer) or the SYQEL account. null = nothing could answer: keep the cached verdict.
 */
export function combineEntitlement(checkout: boolean | null, account: boolean): boolean | null {
  if (account || checkout === true) return true
  return checkout === false ? false : null
}
